import {injectable} from 'inversify';
import {action, computed, makeObservable} from 'mobx';

import type {Nullable} from '../../../../utils/utility-types';

import {platform} from '../../../../utils/platform';

const isWeb = platform.isWeb;

export abstract class BaseNavigationService {
  abstract getQueryParams: () => BaseNavigationService.QueryParams;
  abstract go: (delta: number) => void;
  abstract goBack: () => void;
  abstract navigate: BaseNavigationService.Navigate;
  abstract navigateToNewTab: (to: BaseNavigationService.To) => void;
  abstract setupNavigation: (
    navigation: BaseNavigationService.Navigation,
  ) => void;
  abstract writeQueryParams: (
    params: Record<string, Nullable<string>>,
    options?: {replace?: boolean},
  ) => void;
}

@injectable()
export class BaseNavigationServiceImpl implements BaseNavigationService {
  navigation: Nullable<BaseNavigationService.Navigation> = null;

  constructor() {
    makeObservable(this, {
      go: action.bound,
      goBack: action.bound,
      navigate: computed,
      navigateToNewTab: action.bound,
      setupNavigation: action.bound,
      writeQueryParams: action.bound,
    });
  }

  get navigate() {
    if (!this.navigation) {
      throw new Error('[BaseNavigationService] navigation is not set');
    }

    return this.navigation.navigate;
  }

  getQueryParams() {
    if (!isWeb) {
      return new Map<string, string>();
    }

    const search = window.location.search;

    return new Map(new URLSearchParams(search).entries());
  }

  go(delta: number) {
    this.navigate(delta);
  }

  goBack() {
    this.go(-1);
  }

  navigateToNewTab(to: BaseNavigationService.To) {
    if (!isWeb) {
      return;
    }

    const path = typeof to === 'string' ? to : `${to.pathname}${to.search}`;

    window.open(path, '_blank');
  }

  setupNavigation(navigation: BaseNavigationService.Navigation) {
    this.navigation = navigation;
  }

  writeQueryParams(params: Record<string, Nullable<string>>) {
    if (!isWeb) {
      return;
    }

    const {nextParams, shouldSkip} = this.buildNextQueryParams(params);

    if (shouldSkip) {
      return;
    }

    const searchParamsString = new URLSearchParams(
      Object.fromEntries(nextParams.entries()),
    ).toString();

    const newUrl = [window.location.pathname, searchParamsString]
      .filter(Boolean)
      .join('?');

    window.history.pushState(window.history.state, '', newUrl);
  }

  private buildNextQueryParams(params: Record<string, Nullable<string>>) {
    const prevParams = this.getQueryParams();

    const nextParams = new Map(prevParams);

    for (const paramKey in params) {
      const value = params[paramKey];

      if (value == null || value === '') {
        nextParams.delete(paramKey);
      } else {
        nextParams.set(paramKey, value);
      }
    }

    const shouldSkip = prevParams.size === 0 && nextParams.size === 0;

    return {nextParams, shouldSkip};
  }
}

export namespace BaseNavigationService {
  export interface Navigate {
    (to: To, options?: {replace?: boolean; state?: any}): void;
    (delta: number): void;
  }

  export type Navigation = {
    navigate: Navigate;
  };
  export interface Path {
    /**
     * A URL fragment identifier, beginning with a #.
     */
    hash: string;
    /**
     * A URL pathname, beginning with a /.
     */
    pathname: string;
    /**
     * A URL search string, beginning with a ?.
     */
    search: string;
  }

  export type QueryParams = Map<string, string>;

  export type To =
    | (Omit<Partial<Path>, 'pathname'> & {pathname: string})
    | string;
}
